/**
 * AttorneyAnalysisTab — Attorney lens analysis for the Analysis stage
 *
 * Phase 4.4 — redesign-p4-analatt-44b
 * Wires to existing /api/v1/attorney/analyze/{reportId} endpoint
 * Uses FinancialValue, MetricCard, StatusBadge primitives per design spec
 */

import React, { useEffect, useState } from 'react';
import { Scale, TrendingUp, AlertTriangle, Target, Users, RefreshCw, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { SupersededAnalysisNote } from '@/components/shared/SupersededAnalysisNote';
import { api } from '@/utils/api';
import { BRAND_NAME } from '@/config/brand';

interface UnclaimedLoss {
  category: string;
  title: string;
  description: string;
  dollar_impact: number;
  why_recoverable: string;
  confidence: string;
  attorney_tasks?: string[];
  client_tasks?: string[];
  evidence_needed?: string[];
  insurance_response?: string;
  counter_argument?: string;
}

interface CriticalVulnerability {
  issue_number: number;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  the_problem: string;
  why_it_matters: string;
  financial_risk: number;
  the_fix: {
    steps: string[];
    deadline: string;
    required_docs: string[];
  };
  fallback_position?: string;
}

interface SettlementStrategy {
  current_claim: number;
  potential_claim: number;
  additional_recovery: number;
  claim_strength: string;
  opening_demand: number;
  target_settlement: number;
  walk_away: number;
  leverage_points: string[];
  expected_insurance_response: string;
}

interface AttorneyAnalysisData {
  success: boolean;
  cached: boolean;
  analysis_id?: string;
  analyzed_at?: string;
  claim_amount?: number;
  potential_claim?: number;
  additional_recovery?: number;
  executive_summary?: string;
  case_strength?: string;
  settlement_probability?: string;
  unclaimed_losses?: UnclaimedLoss[];
  total_unclaimed?: number;
  vulnerabilities?: CriticalVulnerability[];
  total_risk?: number;
  settlement_strategy?: SettlementStrategy;
  client_actions?: {
    immediate: string[];
    this_week: string[];
    next_week: string[];
  };
  recommendations?: string[];
  error?: string;
  currency?: {
    superseded?: boolean;
    current_run_id?: string | null;
    current_run_at?: string | null;
    analysis_at?: string | null;
  } | null;
}

interface AttorneyAnalysisTabProps {
  reportId: string;
}

export function AttorneyAnalysisTab({ reportId }: AttorneyAnalysisTabProps) {
  const [analysis, setAnalysis] = useState<AttorneyAnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    unclaimed: true,
    vulnerabilities: true,
    strategy: false,
    actions: false,
  });

  const runAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.post<AttorneyAnalysisData>(
        `/api/v1/attorney/analyze/${reportId}?force_refresh=${forceRefresh}`,
        {}
      );
      setAnalysis(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to run attorney analysis');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<AttorneyAnalysisData>(
        `/api/v1/attorney/analysis/${reportId}`,
        { showErrorToast: false }
      );
      setAnalysis(data);
    } catch (err: any) {
      if (err.status === 404) {
        setAnalysis(null);
      } else {
        setError(err?.message || 'Failed to load attorney analysis');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [reportId]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const getCaseStrengthVariant = (strength?: string): Parameters<typeof StatusBadge>[0]['variant'] => {
    switch (strength?.toUpperCase()) {
      case 'STRONG':
        return 'done';
      case 'MODERATE':
        return 'neutral';
      case 'WEAK':
      case 'AT_RISK':
        return 'attention';
      default:
        return 'neutral';
    }
  };

  const getSeverityVariant = (severity?: string): Parameters<typeof StatusBadge>[0]['variant'] => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return 'attention';
      case 'medium':
        return 'warning';
      case 'low':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  if (loading && !analysis) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-brand)]" />
          <span className="ml-3 text-sm text-[var(--color-text-secondary)]">
            Building case analysis…
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="rounded-lg bg-[rgba(255,102,0,0.08)] p-4">
          <p className="text-sm text-[var(--color-text-primary)]">{error}</p>
          <button
            onClick={() => runAnalysis(true)}
            className="mt-3 rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)]"
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="py-12 text-center">
          <Scale className="mx-auto h-8 w-8 text-[var(--color-text-ghost)]" />
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">
            No attorney analysis available yet.
          </p>
          <button
            onClick={() => runAnalysis(false)}
            disabled={loading}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)] disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Run attorney analysis
          </button>
        </div>
      </div>
    );
  }

  const {
    executive_summary,
    case_strength,
    settlement_probability,
    claim_amount,
    potential_claim,
    additional_recovery,
    unclaimed_losses,
    total_unclaimed,
    vulnerabilities,
    total_risk,
    settlement_strategy,
    client_actions,
    recommendations,
    cached,
    analyzed_at,
  } = analysis;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-[var(--color-needs-human-tint)] p-2">
            <Scale className="h-5 w-5 text-[var(--color-brand)]" />
          </div>
          <div>
            <h2
              className="text-lg font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              Attorney Analysis
            </h2>
            <p className="text-xs text-[var(--color-text-muted)]">
              {cached ? 'Cached' : 'Fresh analysis'} • {analyzed_at ? new Date(analyzed_at).toLocaleString() : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => runAnalysis(true)}
          disabled={loading}
          className="flex items-center gap-2 rounded-md border border-[var(--color-brand)] px-3 py-1.5 text-sm font-medium text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)] disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Re-analyze
        </button>
      </div>

      {/* Superseded analysis notice */}
      <SupersededAnalysisNote currency={analysis.currency} />

      {/* Executive Summary */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3
              className="mb-2 text-base font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              Case Analysis Summary
            </h3>
            <p className="text-sm text-[var(--color-text-secondary)]">{executive_summary}</p>
          </div>
          <div className="ml-4 flex flex-col items-end gap-2">
            {case_strength && (
              <StatusBadge
                text={case_strength.replace('_', ' ')}
                variant={getCaseStrengthVariant(case_strength)}
              />
            )}
            {settlement_probability && (
              <span className="text-xs text-[var(--color-text-muted)]">
                {settlement_probability} settlement probability
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Financial Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          label="Current Claim"
          value={<FinancialValue value={claim_amount} size="lg" />}
          badge={undefined}
        />
        <MetricCard
          label="Potential Claim"
          value={<FinancialValue value={potential_claim} size="lg" />}
          accent
        />
        <MetricCard
          label="Additional Recovery"
          value={<FinancialValue value={additional_recovery} size="lg" />}
          badge={{ text: 'Opportunity', variant: 'active' }}
        />
      </div>

      {/* Unclaimed Losses */}
      {unclaimed_losses && unclaimed_losses.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <button
            onClick={() => toggleSection('unclaimed')}
            className="flex w-full items-center justify-between p-4 hover:bg-[var(--color-bg-hover)]"
          >
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-[var(--color-brand)]" />
              <span className="font-medium text-[var(--color-text-primary)]">
                Unclaimed Losses: <FinancialValue value={total_unclaimed} />
              </span>
            </div>
            {expandedSections.unclaimed ? (
              <ChevronUp className="h-5 w-5 text-[var(--color-text-muted)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--color-text-muted)]" />
            )}
          </button>

          {expandedSections.unclaimed && (
            <div className="space-y-3 border-t border-[var(--color-border-sub)] p-4">
              {unclaimed_losses.map((loss, idx) => (
                <div
                  key={idx}
                  className="rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-bg-elevated)] p-4"
                >
                  <div className="mb-3 flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <TrendingUp className="mt-0.5 h-4 w-4 text-[var(--color-brand)]" />
                      <div>
                        <h4 className="font-medium text-[var(--color-text-primary)]">{loss.title}</h4>
                        <span className="mt-1 inline-block rounded-full bg-[var(--color-needs-human-tint)] px-2 py-0.5 text-xs text-[var(--color-brand)]">
                          {loss.category}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <FinancialValue value={loss.dollar_impact} size="lg" />
                      <StatusBadge
                        text={`${loss.confidence} confidence`}
                        variant={loss.confidence.toLowerCase() === 'high' ? 'done' : 'neutral'}
                        size="sm"
                      />
                    </div>
                  </div>

                  <p className="mb-3 text-sm text-[var(--color-text-secondary)]">{loss.description}</p>

                  <div className="rounded-md bg-[var(--color-bg-base)] p-3">
                    <p className="text-xs font-medium text-[var(--color-text-muted)]">Why Recoverable:</p>
                    <p className="text-sm text-[var(--color-text-secondary)]">{loss.why_recoverable}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Critical Vulnerabilities */}
      {vulnerabilities && vulnerabilities.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <button
            onClick={() => toggleSection('vulnerabilities')}
            className="flex w-full items-center justify-between p-4 hover:bg-[var(--color-bg-hover)]"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-[var(--color-brand)]" />
              <span className="font-medium text-[var(--color-text-primary)]">
                Critical Vulnerabilities ({vulnerabilities.length})
              </span>
            </div>
            {expandedSections.vulnerabilities ? (
              <ChevronUp className="h-5 w-5 text-[var(--color-text-muted)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--color-text-muted)]" />
            )}
          </button>

          {expandedSections.vulnerabilities && (
            <div className="space-y-3 border-t border-[var(--color-border-sub)] p-4">
              {vulnerabilities.map((vuln, idx) => (
                <div
                  key={idx}
                  className="rounded-lg border-l-[3px] border-l-[var(--color-brand)] bg-[var(--color-bg-elevated)] p-4"
                >
                  <div className="mb-3 flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-[var(--color-text-primary)]">
                        Issue #{vuln.issue_number}: {vuln.title}
                      </h4>
                      <StatusBadge
                        text={vuln.severity}
                        variant={getSeverityVariant(vuln.severity)}
                        size="sm"
                      />
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-[var(--color-text-muted)]">Risk:</span>
                      <FinancialValue value={vuln.financial_risk} />
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <p className="text-[var(--color-text-secondary)]">
                      <span className="font-medium text-[var(--color-text-primary)]">Problem:</span>{' '}
                      {vuln.the_problem}
                    </p>
                    <p className="text-[var(--color-text-secondary)]">
                      <span className="font-medium text-[var(--color-text-primary)]">Impact:</span>{' '}
                      {vuln.why_it_matters}
                    </p>
                    {vuln.the_fix.steps.length > 0 && (
                      <div className="mt-2 rounded-md bg-[var(--color-bg-base)] p-3">
                        <p className="mb-1 text-xs font-medium text-[var(--color-brand)]">Fix Steps:</p>
                        <ol className="list-inside list-decimal text-xs text-[var(--color-text-secondary)]">
                          {vuln.the_fix.steps.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settlement Strategy */}
      {settlement_strategy && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <button
            onClick={() => toggleSection('strategy')}
            className="flex w-full items-center justify-between p-4 hover:bg-[var(--color-bg-hover)]"
          >
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-[var(--color-brand)]" />
              <span className="font-medium text-[var(--color-text-primary)]">Settlement Strategy</span>
            </div>
            {expandedSections.strategy ? (
              <ChevronUp className="h-5 w-5 text-[var(--color-text-muted)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--color-text-muted)]" />
            )}
          </button>

          {expandedSections.strategy && (
            <div className="grid grid-cols-1 gap-4 border-t border-[var(--color-border-sub)] p-4 sm:grid-cols-3">
              <MetricCard
                label="Opening Demand"
                value={<FinancialValue value={settlement_strategy.opening_demand} />}
              />
              <MetricCard
                label="Target Settlement"
                value={<FinancialValue value={settlement_strategy.target_settlement} />}
                badge={{ text: 'Goal', variant: 'done' }}
              />
              <MetricCard
                label="Walk Away"
                value={<FinancialValue value={settlement_strategy.walk_away} />}
              />
            </div>
          )}
        </div>
      )}

      {/* Client Actions */}
      {client_actions && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <button
            onClick={() => toggleSection('actions')}
            className="flex w-full items-center justify-between p-4 hover:bg-[var(--color-bg-hover)]"
          >
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-[var(--color-brand)]" />
              <span className="font-medium text-[var(--color-text-primary)]">Client Action Plan</span>
            </div>
            {expandedSections.actions ? (
              <ChevronUp className="h-5 w-5 text-[var(--color-text-muted)]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[var(--color-text-muted)]" />
            )}
          </button>

          {expandedSections.actions && (
            <div className="grid grid-cols-1 gap-4 border-t border-[var(--color-border-sub)] p-4 md:grid-cols-3">
              {client_actions.immediate && client_actions.immediate.length > 0 && (
                <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3">
                  <p className="mb-2 text-sm font-medium text-[var(--color-brand)]">Immediate (24-48hrs)</p>
                  <ul className="space-y-1">
                    {client_actions.immediate.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
                        <span className="text-[var(--color-brand)]">→</span> {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {client_actions.this_week && client_actions.this_week.length > 0 && (
                <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3">
                  <p className="mb-2 text-sm font-medium text-[var(--color-text-secondary)]">This Week</p>
                  <ul className="space-y-1">
                    {client_actions.this_week.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
                        <span className="text-[var(--color-text-muted)]">→</span> {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {client_actions.next_week && client_actions.next_week.length > 0 && (
                <div className="rounded-lg bg-[var(--color-bg-elevated)] p-3">
                  <p className="mb-2 text-sm font-medium text-[var(--color-text-muted)]">Next Week</p>
                  <ul className="space-y-1">
                    {client_actions.next_week.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
                        <span className="text-[var(--color-text-dim)]">→</span> {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="rounded-xl border border-[var(--color-needs-human-tint-bd)] bg-[var(--color-needs-human-tint)] p-4">
          <h3 className="mb-3 flex items-center gap-2 font-medium text-[var(--color-brand)]">
            <Scale className="h-4 w-4" />
            Case Strategy Recommendations
          </h3>
          <ul className="space-y-2">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
                <span className="text-[var(--color-brand)]">→</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default AttorneyAnalysisTab;
